import {
  useInfiniteQuery,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
} from 'constants/appConstants';
import {
  MSearchFilter,
  MSearchParam,
  MSearchResponse,
} from 'interfaces/IMasterDb';
import { MasterDbService } from 'services/MasterDbServices';

const NO_EXTRA_FILTERS: MSearchFilter[][] = [];

// extraFilters are ANDed after the name match (each inner array is an OR
// group) and are part of the query key, so differently-scoped searches over
// the same collection never share a cache entry.
export const useMSearchCollection = <T>(
  gCollectionName: string,
  gOrgId: string | null,
  searchTerm: string,
  queryKey: unknown[],
  extraFilters: MSearchFilter[][] = NO_EXTRA_FILTERS
) => {
  return useInfiniteQuery<
    MSearchResponse<T>,
    Error,
    InfiniteData<MSearchResponse<T>>,
    QueryKey,
    number
  >({
    queryKey: (extraFilters.length > 0
      ? [...queryKey, gOrgId, searchTerm, extraFilters]
      : [...queryKey, gOrgId, searchTerm]) as QueryKey,
    queryFn: ({ pageParam }): Promise<MSearchResponse<T>> => {
      const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const params: MSearchParam = {
        collection: gCollectionName,
        and: [
          [{ field: 'name', operator: 'r~', value: escapedTerm }],
          ...extraFilters,
        ],
        pageSize: DEFAULT_PAGE_SIZE,
        pageIndex: pageParam,
      };
      return MasterDbService.SearchMCollectionData<T>(params);
    },
    initialPageParam: DEFAULT_PAGE_INDEX,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + 1 : undefined,
    enabled: !!gOrgId && searchTerm.length > 0,
    staleTime: 1000 * 60 * 5,
  });
};
